import { Link, useLocation } from "react-router-dom";
import { PageHeader } from "../../components/ui";

/**
 * The page for an address that does not resolve.
 *
 * Unknown routes used to redirect silently to Home. A citation deep-link into
 * a superseded document, a bookmark from before a rename, a mistyped address —
 * all of them landed the reader on the dashboard with no indication that
 * anything had gone wrong, which reads as "the link worked and the document is
 * missing" rather than "this address does not exist".
 *
 * It also matters for a document management system specifically: an engineer
 * following a link to a procedure needs to know whether the procedure is gone
 * or the link is wrong, because those have different next steps.
 */
export function NotFoundScreen({
  title = "That page does not exist",
  detail,
}: {
  title?: string;
  detail?: string;
}) {
  const location = useLocation();

  return (
    <>
      <PageHeader eyebrow="Not found" title={title} />

      <div className="card mx-auto max-w-2xl px-8 py-10">
        <p className="text-base+ leading-[1.7] text-prose">
          {detail ??
            "The address did not match anything in the compendium. The document may have been renamed or archived, or the link may be incomplete."}
        </p>

        <p className="mt-3 break-all rounded-field bg-surface-subtle px-3 py-2 font-mono text-sm text-muted">
          {location.pathname}
          {location.search}
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="/documents" className="btn-dark no-underline">
            Browse the library
          </Link>
          <Link to="/search" className="btn-small no-underline">
            Search the compendium
          </Link>
          <Link to="/assistant" className="btn-small no-underline">
            Ask Compendium Discover
          </Link>
        </div>

        <p className="mt-5 border-t border-line-soft pt-4 text-sm leading-[1.6] text-muted-soft">
          If you followed this link from inside the application, it is worth
          reporting — a link that goes nowhere from a screen that should know
          better is a defect, not a typo.
        </p>
      </div>
    </>
  );
}
